/**
 * Adapts a `DashboardDataSource` into the `Layer` + `AppModule` shape the
 * main Application Builder's `SymbologyPanel`/`LabelProperties` editors
 * expect, so the dashboard can reuse those exact components rather than
 * building a second symbology/labeling UI.
 *
 * Both editors are self-contained — they only ever read `layer.id`,
 * `layer.name`, `layer.geometryType`, `layer.localData`,
 * `layer.arcgisLayerId`, `layer.renderer`, and `module.geoServerUrl` /
 * `module.arcgisUrl` (the last two by way of `detectLayerSource` in
 * `layers/capabilities.ts`, and their own WFS/ArcGIS field-fetching) — so a
 * minimal stand-in for each is enough; nothing here needs the real builder
 * context.
 */

import type { AppModule, Layer, LayerRenderer } from '../../types';
import type { LayerLabeling } from '../../labeling/LabelTypes';
import type { DashboardDataSource } from '../types/dashboardTypes';

/** A `Layer` standing in for one data source, carrying whatever style this
 *  WIDGET has already set for it so the editor opens showing it. */
export function dashboardSourceAsLayer(
  source: DashboardDataSource,
  style?: { renderer?: LayerRenderer; labeling?: LayerLabeling },
): Layer {
  return {
    id: source.id,
    name: source.layerName,
    sublayers: [],
    checked: true,
    expanded: false,
    arcgisLayerId: source.arcgisLayerId,
    localData: source.localData,
    geometryType: source.geometryType,
    renderer: style?.renderer,
    labeling: style?.labeling,
  };
}

/**
 * A module stub carrying just the ONE service URL this specific source
 * uses. Deliberately not a dashboard-wide value: unlike `AppModule`, which
 * holds a single `geoServerUrl`/`arcgisUrl` for its whole map,
 * `DashboardDataSource` carries its own `serviceUrl` per source (a
 * dashboard pulls from several services at once) — see that type's own doc
 * comment in `dashboardTypes.ts`.
 */
export function dashboardModuleStub(source: DashboardDataSource): AppModule {
  return {
    id: '',
    name: '',
    title: '',
    built: true,
    layers: [],
    tools: [],
    geoServerUrl: source.serviceType === 'geoserver' ? source.serviceUrl : '',
    geoServerLayers: [],
    arcgisUrl: source.serviceType === 'arcgis' ? source.serviceUrl : '',
    homeCenter: [0, 0],
    homeZoom: 0,
  };
}
